// src/pages/home/PlayerRegister.jsx
import React, { useState, useContext, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import API from "../../api/api";
import Navbar from "../../components/Navbar";
import { validateAadhaar } from "../../components/common/AadhaarValidation";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import SearchableSelect from "../../components/common/SearchableSelect";
import DisabilitySelect from "../../components/common/DisabilitySelect";
import DistrictContactsModal from "../../components/common/DistrictContactsModal";
import { FaSpinner, FaEye, FaEyeSlash, FaPhoneAlt, FaExclamationTriangle, FaAddressBook, FaUserTie } from "react-icons/fa";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const OTP_DEFAULT_RESEND_SECONDS = 120;

const normalizeMobile = (value) => {
  if (!value) return "";
  let digits = value.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("0")) {
    digits = digits.substring(1);
  } else if (digits.length === 12 && digits.startsWith("91")) {
    digits = digits.substring(2);
  }
  return digits.substring(0, 10);
};

const AP_DISTRICTS = [
  "Alluri Sitharama Raju",
  "Anakapalli",
  "Ananthapur",
  "Annamayya",
  "Bapatla",
  "Chittoor",
  "Dr. B. R. Ambedkar Konaseema",
  "East Godavari",
  "Eluru",
  "Guntur",
  "Kakinada",
  "Krishna",
  "Kurnool",
  "Markapuram",
  "Nandyal",
  "NTR",
  "Palnadu",
  "Parvathipuram Manyam",
  "Polavaram",
  "Prakasam",
  "Sri Potti Sriramulu Nellore",
  "Sri Sathya Sai",
  "Srikakulam",
  "Tirupati",
  "Visakhapatnam",
  "Vizianagaram",
  "West Godavari",
  "YSR Kadapa"
];

const formatCountdown = (seconds) => {
  const total = Math.max(0, Number(seconds || 0));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const FileTile = ({ preview, label }) => {
  if (!preview) return <div className="text-xs text-slate-400">No file</div>;
  if (preview.type === 'image') {
    return (
      <div className="w-full">
        <img src={preview.url} alt={label || 'Preview'} className="w-full h-24 object-cover rounded-lg border border-slate-200" />
        <p className="text-[10px] text-slate-500 mt-1 truncate text-center" title={preview.name}>{preview.name}</p>
      </div>
    );
  }
  // PDF or other document — show embedded thumbnail
  return (
    <div className="w-full">
      <div className="relative w-full h-24 rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
        <object
          data={preview.url + '#toolbar=0&navpanes=0&scrollbar=0&view=FitH'}
          type="application/pdf"
          className="w-full h-full"
          style={{ pointerEvents: 'none' }}
        >
          <div className="w-full h-full flex flex-col items-center justify-center bg-red-50">
            <span className="text-2xl">📄</span>
          </div>
        </object>
      </div>
      <p className="text-[10px] text-slate-500 mt-1 truncate text-center" title={preview.name}>{preview.name}</p>
    </div>
  );
};

FileTile.propTypes = {
  label: PropTypes.string,
  preview: PropTypes.shape({
    type: PropTypes.string,
    url: PropTypes.string,
    name: PropTypes.string,
  }),
};

const FormSection = ({ title, number, children }) => (
  <div className="border border-slate-200 rounded-xl p-6">
    <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-3">
      <span className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center text-sm font-bold">{number}</span>
      {title}
    </h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
      {children}
    </div>
  </div>
);

FormSection.propTypes = {
  title: PropTypes.string.isRequired,
  number: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
};

const Input = React.forwardRef(({ label, name, error, className, ...props }, ref) => (
  <div className={className}>
    <label htmlFor={name} className="block text-sm font-medium text-slate-700 mb-1">
      {label}
    </label>
    <input
      id={name}
      name={name}
      ref={ref}
      className={`w-full px-4 py-2 bg-slate-100 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-shadow ${error ? 'border-red-500 ring-1 ring-red-500' : ''}`}
      {...props}
    />
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
));

const PasswordInput = ({ label, name, value, onChange, error, required, minLength }) => {
  const [show, setShow] = React.useState(false);
  return (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-slate-700 mb-1">
        {label}
      </label>
      <div className="relative">
        <input
          id={name}
          name={name}
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          required={required}
          minLength={minLength}
          className={`w-full px-4 py-2 pr-11 bg-slate-100 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-shadow ${error ? 'border-red-500 ring-1 ring-red-500' : ''}`}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow((s) => !s)}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-slate-800 transition-colors"
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
        </button>
      </div>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
};

const FileInput = ({ label, name, icon, preview, onClear, ...props }) => (
  <div className="text-center">
    <label className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
    <div className="relative w-full h-32 bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center">
      {preview ? (
        <>
          {preview.type === 'image' ? (
            <img src={preview.url} alt="Preview" className="w-full h-full object-cover rounded-lg" />
          ) : (
            <div className="text-center p-2">
              <span className="text-4xl">📄</span>
              <p className="text-xs text-slate-600 break-all">{preview.name}</p>
            </div>
          )}
          <button type="button" onClick={onClear} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs shadow-lg">&times;</button>
        </>
      ) : (
        <div className="text-slate-400">{icon}</div>
      )}
    </div>
    <label className="w-full mt-2 inline-block py-2 px-3 rounded-lg font-semibold text-sm bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-pointer transition-colors">
      <input type="file" name={name} className="hidden" {...props} />
      {preview ? 'Change File' : `Upload ${label}`}
    </label>
  </div>
);


const PlayerRegister = () => {
  const { setToken, setRole, setUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ name: "", relation: "", dob: "", gender: "", disability_types: [], mobile: "", aadhaar_number: "", email: "", email_otp: "", password: "", confirm_password: "", district: "", mandal: "", postoffice: "", club: "", coach: "", address: "", state: "Andhra Pradesh", pincode: "", photo: null, aadhaar: null, birth_certificate: null, sadarem: null, others: null });
  const [registrationSports, setRegistrationSports] = useState([]);
  const [sportProfiles, setSportProfiles] = useState([]);
  const [classificationsBySport, setClassificationsBySport] = useState({});
  const [loading, setLoading] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpEnabled, setOtpEnabled] = useState(true);
  const [pincodeOptions, setPincodeOptions] = useState([]);

  useEffect(() => {
    API.get("/sports").then((res) => {
      const rows = res?.data || res || [];
      setRegistrationSports(Array.isArray(rows) ? rows : []);
    }).catch(() => setRegistrationSports([]));
  }, []);

  const toggleSport = async (sportId, checked) => {
    const id = Number(sportId);
    if (!checked) {
      setSportProfiles((current) => current.filter((profile) => profile.sport_id !== id));
      return;
    }

    setSportProfiles((current) => current.some((profile) => profile.sport_id === id)
      ? current
      : [...current, { sport_id: id, classification_id: "", classification_type: "New", certificate: null }]);

    if (!classificationsBySport[id]) {
      try {
        const res = await API.get(`/registration/sports/${id}/options`);
        setClassificationsBySport((current) => ({
          ...current,
          [id]: Array.isArray(res?.classifications) ? res.classifications : [],
        }));
      } catch {
        setClassificationsBySport((current) => ({ ...current, [id]: [] }));
      }
    }
  };

  const updateSportProfile = (sportId, changes) => {
    setSportProfiles((current) => current.map((profile) =>
      profile.sport_id === Number(sportId) ? { ...profile, ...changes } : profile
    ));
  };

  useEffect(() => {
    API.fetchPublicOtpSettings()
      .then((res) => {
        setOtpEnabled(!!res?.enable_registration_otp);
      })
      .catch((err) => {
        console.error("Failed to load public OTP settings:", err);
      });
  }, []);
  const [otpSent, setOtpSent] = useState(false);
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);
  const [otpNotice, setOtpNotice] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [previews, setPreviews] = useState({});
  const [errors, setErrors] = useState({ email: "", email_otp: "", aadhaar_number: "", mobile: "" });
  const [selectedPostOffice, setSelectedPostOffice] = useState("");
  const [aadhaarConsent, setAadhaarConsent] = useState(false);
  const [showDistrictModal, setShowDistrictModal] = useState(false);
  const [districtDirectory, setDistrictDirectory] = useState([]);

  useEffect(() => {
    API.fetchDistrictContacts()
      .then((res) => {
        if (res?.districts) setDistrictDirectory(res.districts);
      })
      .catch((err) => {
        console.error("Failed to load district contacts in registration:", err);
      });
  }, []);

  const selectedDistrictInfo = useMemo(() => {
    if (!formData.district) return null;
    return districtDirectory.find(
      (d) => d.district_name.toLowerCase() === String(formData.district).toLowerCase()
    ) || null;
  }, [formData.district, districtDirectory]);

  const emailRef = useRef(null);
  const aadhaarRef = useRef(null);
  const otpVerifyRequestIdRef = useRef(0);
  const otpLastVerifyKeyRef = useRef("");

  // --- Effects ---

  // Keep a ref of active blob URLs so we can revoke them on unmount or explicit clear
  // without triggering re-renders or premature revocation.
  const blobUrlsRef = useRef({});

  // Revoke all blob URLs on component unmount only
  useEffect(() => {
    const currentBlobUrls = blobUrlsRef.current;
    return () => {
      Object.values(currentBlobUrls).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);

  // Pincode lookup
  useEffect(() => {
    const pincode = String(formData.pincode || "").trim();
    if (pincode.length !== 6) {
      setPincodeOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const rows = await API.lookupPincode(pincode);
        setPincodeOptions(Array.isArray(rows) ? rows : []);
        if (rows.length === 1) {
          const one = rows[0];
          setSelectedPostOffice(one.postoffice || "");
          setFormData(prev => ({ ...prev, postoffice: one.postoffice || "", mandal: one.mandal || "", state: one.state || "Andhra Pradesh" }));
        }
      } catch (err) {
        setPincodeOptions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [formData.pincode]);

  useEffect(() => {
    const email = String(formData.email || "").trim();
    const otp = String(formData.email_otp || "").trim();

    if (!emailRegex.test(email) || otp.length !== 6 || otpVerified) {
      if (otp.length !== 6) {
        otpLastVerifyKeyRef.current = "";
      }
      if (!otpVerified) {
        otpVerifyRequestIdRef.current += 1;
        setOtpVerifying(false);
      }
      return;
    }

    const verifyKey = `${email.toLowerCase()}|${otp}`;
    if (otpLastVerifyKeyRef.current === verifyKey) {
      return;
    }
    otpLastVerifyKeyRef.current = verifyKey;

    const requestId = ++otpVerifyRequestIdRef.current;
    setOtpVerifying(true);
    setOtpVerified(false);

    (async () => {
      try {
        await API.verifyPlayerRegistrationOtp(email, otp);
        if (otpVerifyRequestIdRef.current !== requestId) return;

        setOtpVerified(true);
        setOtpResendCooldown(0);
        setErrors(prev => ({ ...prev, email_otp: "" }));
        setOtpNotice("OTP verified successfully. You can continue registration.");
      } catch (err) {
        if (otpVerifyRequestIdRef.current !== requestId) return;

        if ((err?.status || 0) >= 500 || err?.code === "NO_RESPONSE") {
          otpLastVerifyKeyRef.current = "";
        }
        setOtpVerified(false);
        const message = err?.message || "Invalid OTP. Please resend OTP and try again.";
        setErrors(prev => ({ ...prev, email_otp: message }));
        setOtpNotice("OTP verification failed. Please resend OTP.");
      } finally {
        if (otpVerifyRequestIdRef.current === requestId) {
          setOtpVerifying(false);
        }
      }
    })();
  }, [formData.email, formData.email_otp, otpVerified]);

  useEffect(() => {
    if (otpResendCooldown <= 0 || otpVerified) return;
    const timer = setInterval(() => {
      setOtpResendCooldown(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpResendCooldown, otpVerified]);

  // --- Handlers ---

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      const file = files[0];
      setFormData(prev => ({ ...prev, [name]: file }));
      if (file) {
        // Revoke previous blob URL if any
        if (blobUrlsRef.current[name]) {
          URL.revokeObjectURL(blobUrlsRef.current[name]);
        }
        const isImage = file.type.startsWith('image/');
        const url = URL.createObjectURL(file);
        // Track the blob URL in ref (not state) to prevent premature revocation
        blobUrlsRef.current[name] = url;
        setPreviews(prev => ({
          ...prev,
          [name]: {
            name: file.name,
            type: isImage ? 'image' : 'file',
            url
          }
        }));
      }
    } else {
      if (name === "aadhaar_number") {
        const cleaned = String(value || "").replace(/\D/g, "").slice(0, 12);
        setFormData(prev => ({ ...prev, aadhaar_number: cleaned }));
        setErrors(e => ({ ...e, aadhaar_number: "" }));
        return;
      }
      if (name === "mobile") {
        const cleaned = normalizeMobile(value);
        setFormData(prev => ({ ...prev, mobile: cleaned }));
        setErrors(e => ({ ...e, mobile: "" }));
        return;
      }
      if (name === "email_otp") {
        const cleaned = String(value || "").replace(/\D/g, "").slice(0, 6);
        setFormData(prev => ({ ...prev, email_otp: cleaned }));
        setOtpVerified(false);
        setErrors(e => ({ ...e, email_otp: "" }));
        return;
      }
      if (name === "email") {
        setFormData(prev => ({ ...prev, email: value, email_otp: "" }));
        setOtpVerified(false);
        setOtpSent(false);
        setOtpResendCooldown(0);
        otpLastVerifyKeyRef.current = "";
        setErrors(e => ({ ...e, email: "", email_otp: "" }));
        setOtpNotice("");
        return;
      }
      let val = value;
      if (!["password", "confirm_password", "dob", "gender", "email", "email_otp", "aadhaar_number", "mobile", "pincode"].includes(name)) {
        val = String(value || "").toUpperCase();
      }
      setFormData(prev => ({ ...prev, [name]: val }));
    }
  };

  const handlePincodeChange = (e) => {
    const clean = e.target.value.replace(/\D/g, "").slice(0, 6);
    setFormData(prev => ({ ...prev, pincode: clean, mandal: "", postoffice: "" }));
    setSelectedPostOffice("");
  }

  const handlePostOfficeChange = (e) => {
    const val = e.target.value;
    setSelectedPostOffice(val);
    const selected = pincodeOptions.find(opt => opt.postoffice === val);
    if (selected) {
      setFormData(prev => ({
        ...prev,
        postoffice: String(val || "").toUpperCase(),
        mandal: String(selected.mandal || "").toUpperCase(),
        state: String(selected.state || "").toUpperCase()
      }));
    }
  };

  const handleClearFile = (name) => {
    // Revoke blob URL via ref (not state) to avoid stale closure issues
    if (blobUrlsRef.current[name]) {
      URL.revokeObjectURL(blobUrlsRef.current[name]);
      delete blobUrlsRef.current[name];
    }
    setFormData(prev => ({ ...prev, [name]: null }));
    setPreviews(prev => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const checkEmailAvailability = async (rawEmail) => {
    const email = String(rawEmail || "").trim();
    if (!email || !emailRegex.test(email)) return { ok: false };
    try {
      const res = await API.get("/auth/check-email", { params: { email } });
      if (res?.exists) {
        setErrors(prev => ({ ...prev, email: "Email already registered" }));
        return { ok: false };
      }
      return { ok: true };
    } catch (_err) {
      return { ok: true };
    }
  };

  const checkAadhaarAvailability = async (rawAadhaar) => {
    const aadhaar = String(rawAadhaar || "").trim();
    if (!aadhaar || aadhaar.length !== 12 || !validateAadhaar(aadhaar)) return { ok: false };
    try {
      const res = await API.get("/auth/check-aadhaar", { params: { aadhaar } });
      if (res?.invalid) {
        setErrors(prev => ({ ...prev, aadhaar_number: "Invalid Aadhaar number" }));
        return { ok: false };
      }
      if (res?.exists) {
        setErrors(prev => ({ ...prev, aadhaar_number: "Aadhaar number already registered" }));
        return { ok: false };
      }
      return { ok: true };
    } catch (_err) {
      return { ok: true };
    }
  };

  const checkMobileAvailability = async (rawMobile) => {
    const mobile = String(rawMobile || "").trim();
    if (!mobile || mobile.length < 10) return { ok: false };
    try {
      const res = await API.get("/auth/check-mobile", { params: { mobile } });
      if (res?.exists) {
        setErrors(prev => ({ ...prev, mobile: "Mobile number already registered" }));
        return { ok: false };
      }
      return { ok: true };
    } catch (_err) {
      return { ok: true };
    }
  };

  const handleEmailBlur = async () => {
    const email = String(formData.email || "").trim();
    if (!email) return;
    if (!emailRegex.test(email)) {
      setErrors(prev => ({ ...prev, email: "Invalid email format" }));
      return;
    }
    await checkEmailAvailability(email);
  };

  const handleAadhaarBlur = async () => {
    const aadhaar = String(formData.aadhaar_number || "").trim();
    if (!aadhaar) return;
    if (aadhaar.length !== 12 || !validateAadhaar(aadhaar)) {
      setErrors(prev => ({ ...prev, aadhaar_number: "Invalid Aadhaar number" }));
      return;
    }
    await checkAadhaarAvailability(aadhaar);
  };

  const handleMobileBlur = async () => {
    const mobile = String(formData.mobile || "").trim();
    if (!mobile) return;
    if (mobile.length !== 10 || !/^\d{10}$/.test(mobile)) {
      setErrors(prev => ({ ...prev, mobile: "Enter a valid 10-digit mobile number" }));
      return;
    }
    await checkMobileAvailability(mobile);
  };

  const handleSendEmailOtp = async () => {
    const email = String(formData.email || "").trim();
    if (otpVerified) return;

    if (!emailRegex.test(email)) {
      setErrors(prev => ({ ...prev, email: "Enter a valid email before requesting OTP" }));
      emailRef.current?.focus();
      return;
    }

    const emailCheck = await checkEmailAvailability(email);
    if (!emailCheck.ok) {
      emailRef.current?.focus();
      return;
    }

    setOtpSending(true);
    setOtpVerified(false);

    try {
      const res = await API.sendPlayerRegistrationOtp(email);
      const expiryMinutes = Math.max(1, Math.round(Number(res?.data?.expires_in || 600) / 60));
      const resendAfter = Math.max(
        0,
        Number(res?.data?.resend_after || OTP_DEFAULT_RESEND_SECONDS)
      );

      setOtpSent(true);
      setOtpResendCooldown(resendAfter);
      otpLastVerifyKeyRef.current = "";
      setOtpNotice(`OTP sent to ${email}. It stays valid for about ${expiryMinutes} minutes.`);
      setFormData(prev => ({ ...prev, email_otp: "" }));
      setErrors(prev => ({ ...prev, email_otp: "" }));

      await Swal.fire({
        icon: "success",
        title: "OTP Sent",
        text: `We sent a 6-digit OTP to ${email}.`,
      });
    } catch (err) {
      const message = err?.message || "Please try again in a moment.";
      const retryMatch = String(message).match(/(\d+)\s*seconds?/i);
      if (retryMatch) {
        const retryAfter = Number(retryMatch[1] || 0);
        setOtpSent(true);
        setOtpResendCooldown(retryAfter);
        setOtpNotice(`Please wait ${formatCountdown(retryAfter)} before resending OTP.`);
      } else {
        setOtpNotice("");
      }
      await Swal.fire({
        icon: "error",
        title: "Unable to Send OTP",
        text: message,
      });
    } finally {
      setOtpSending(false);
    }
  };

  const validateForm = async () => {
    const required = ["name", "relation", "dob", "gender", "mobile", "aadhaar_number", "email", "password", "district", "mandal", "address", "state", "pincode", "photo", "aadhaar", "birth_certificate", "sadarem"];
    if (otpEnabled) {
      required.push("email_otp");
    }
    const missing = required.filter(k => !formData[k]?.toString().trim());
    if (missing.length > 0) {
      Swal.fire({ icon: 'warning', title: 'Missing Fields', text: `Please fill out: ${missing.join(", ")}` });
      return false;
    }
    if (!Array.isArray(formData.disability_types) || formData.disability_types.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Disability Type Required', text: 'Please select at least one disability type.' });
      return false;
    }
    if (!sportProfiles.length || sportProfiles.some((profile) => !profile.classification_id)) {
      Swal.fire({ icon: 'warning', title: 'Sports Details Required', text: 'Select at least one sport and its classification.' });
      return false;
    }
    if (sportProfiles.some((profile) => profile.classification_type !== "New" && !profile.certificate)) {
      Swal.fire({ icon: 'warning', title: 'Certificate Required', text: 'Upload the previous classification certificate for every non-new classification.' });
      return false;
    }
    if (!emailRegex.test(formData.email)) {
      setErrors(e => ({ ...e, email: "Invalid email format" }));
      return false;
    }
    if (!validateAadhaar(formData.aadhaar_number)) {
      setErrors(e => ({ ...e, aadhaar_number: "Invalid Aadhaar number" }));
      return false;
    }
    if (otpEnabled) {
      if (!/^\d{6}$/.test(String(formData.email_otp || ""))) {
        setErrors(e => ({ ...e, email_otp: "Enter the 6-digit OTP sent to your email" }));
        return false;
      }
      if (!otpVerified) {
        setErrors(e => ({ ...e, email_otp: "Please enter a valid OTP and wait for verification." }));
        await Swal.fire({
          icon: "warning",
          title: "OTP Not Verified",
          text: "Enter the 6-digit OTP and wait for verification before proceeding.",
        });
        return false;
      }
    }
    if (!/^\d{10}$/.test(String(formData.mobile || "").trim())) {
      setErrors(prev => ({ ...prev, mobile: "Enter a valid 10-digit mobile number" }));
      Swal.fire({ icon: "warning", title: "Invalid Mobile Number", text: "Please enter a valid 10-digit mobile number." });
      return false;
    }
    if (String(formData.password || "").length < 6) {
      Swal.fire({ icon: "warning", title: "Weak Password", text: "Password must be at least 6 characters." });
      return false;
    }
    if (String(formData.confirm_password || "") !== String(formData.password || "")) {
      Swal.fire({ icon: "warning", title: "Password Mismatch", text: "Password and Confirm Password do not match." });
      return false;
    }
    if (!aadhaarConsent) {
      Swal.fire({
        icon: "warning",
        title: "Aadhaar Consent Required",
        text: "Please check the Aadhaar consent and declaration checkbox before submitting your registration.",
      });
      return false;
    }
    const [emailCheck, aadhaarCheck, mobileCheck] = await Promise.all([
      checkEmailAvailability(formData.email),
      checkAadhaarAvailability(formData.aadhaar_number),
      checkMobileAvailability(formData.mobile)
    ]);
    if (!emailCheck.ok || !aadhaarCheck.ok || !mobileCheck.ok) {
      return false;
    }
    return true;
  };

  const handleOpenPreview = async (e) => {
    e.preventDefault();
    if (await validateForm()) {
      setShowPreview(true);
    }
  };

  const submitRegistration = async () => {
    if (!(await validateForm())) return;
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        if (k === "confirm_password") return; // don't send to backend
        if (k === "disability_types") {
          fd.append(k, JSON.stringify(v));
          return;
        }
        if (v) fd.append(k, v);
      });
      fd.append("sport_profiles", JSON.stringify(sportProfiles.map(({ certificate, ...profile }) => profile)));
      sportProfiles.forEach((profile) => {
        if (profile.certificate) {
          fd.append(`sport_certificates[${profile.sport_id}]`, profile.certificate);
        }
      });

      const res = await API.registerPlayer(fd);
      const userData = res?.data;
      const role = userData?.role || "player";

      // Use AuthContext to set auth state
      setUser(userData);
      setToken("cookie-auth");
      setRole(role);

      await Swal.fire({ icon: 'success', title: 'Registration Successful!', text: 'Welcome to  Para Sports Accociation of Andhra Pradesh!', timer: 2000, showConfirmButton: false });
      navigate('/player-profile'); // Redirect to a relevant page
    } catch (err) {
      const serverErrors = err?.errors && typeof err.errors === "object" ? err.errors : {};
      if (serverErrors.email?.[0]) {
        setErrors(prev => ({ ...prev, email: serverErrors.email[0] }));
      }
      if (serverErrors.email_otp?.[0]) {
        setErrors(prev => ({ ...prev, email_otp: serverErrors.email_otp[0] }));
        setOtpVerified(false);
      }
      if (serverErrors.aadhaar_number?.[0]) {
        setErrors(prev => ({ ...prev, aadhaar_number: serverErrors.aadhaar_number[0] }));
      }
      const firstValidationMessage = Object.values(serverErrors).flat()[0];
      const message = err?.message || firstValidationMessage || "An unexpected error occurred.";
      Swal.fire({ icon: 'error', title: 'Registration Failed', text: message });
    } finally {
      setLoading(false);
      setShowPreview(false);
    }
  };

  // --- Derived State ---
  const mandalOptions = useMemo(() => [...new Set(pincodeOptions.map(opt => opt.mandal).filter(Boolean))], [pincodeOptions]);
  const canPreviewRegister = (!otpEnabled || otpVerified) && !otpVerifying && !loading && aadhaarConsent;
  const otpResendLocked = otpSent && otpResendCooldown > 0;
  const otpButtonText = otpSending
    ? "Sending..."
    : otpResendLocked
      ? `Resend in ${formatCountdown(otpResendCooldown)}`
      : otpSent
        ? "Resend OTP"
        : "Send OTP";

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="flex-grow flex items-center justify-center p-4">
        <div className="w-full max-w-6xl mx-auto bg-white shadow-xl rounded-2xl grid md:grid-cols-5 overflow-hidden">

          <div className="hidden md:flex md:col-span-2 flex-col items-center justify-center p-12 bg-blue-600 text-white text-center">
            <img src="/images/parasports.png" alt="Parasports Logo" className="w-24 h-auto mb-6" />
            <h1 className="text-3xl font-bold mb-2">Join the Movement</h1>
            <p className="text-blue-100">Become a part of Andhra Pradesh's proud para sports legacy.</p>
          </div>

          <div className="md:col-span-3 p-8 sm:p-12 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 150px)' }}>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Player Registration</h2>
            <p className="text-slate-600 mb-5">Create your athlete profile to get started.</p>

            {/* Mandatory District Intimation Notice */}
            <div className="bg-gradient-to-br from-amber-500/10 via-amber-50 to-orange-50 border-2 border-amber-400 p-4 sm:p-5 rounded-2xl mb-8 shadow-xs">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2.5 bg-amber-500 text-white rounded-xl shadow-xs shrink-0 mt-0.5">
                    <FaExclamationTriangle className="text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-amber-950 text-sm sm:text-base">
                      Mandatory Prior Intimation to District Association
                    </h3>
                    <p className="text-xs sm:text-sm text-amber-900 mt-1 leading-relaxed">
                      Before applying, you <strong>must contact your corresponding District Secretary or President</strong> for prior intimation. District authorization is mandatory as district officials must approve your registration. <strong>Registrations without prior intimation will not be accepted.</strong>
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDistrictModal(true)}
                  className="shrink-0 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-xs transition"
                >
                  <FaAddressBook /> View District Contacts
                </button>
              </div>
            </div>

            <form onSubmit={handleOpenPreview} className="space-y-8">
              <FormSection title="Personal Information" number={1}>
                <Input label="Full Name *" name="name" value={formData.name} onChange={handleChange} required />
                <Input label="Father/Husband Name *" name="relation" value={formData.relation} onChange={handleChange} required />
                <Input label="Date of Birth *" name="dob" type="date" value={formData.dob} onChange={handleChange} required />
                <div>
                  <label htmlFor="gender" className="block text-sm font-medium text-slate-700 mb-1">Gender *</label>
                  <select id="gender" name="gender" value={formData.gender} onChange={handleChange} required className="w-full px-4 py-2 bg-slate-100 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none">
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <DisabilitySelect
                    selected={formData.disability_types}
                    onChange={(selected) => setFormData((prev) => ({ ...prev, disability_types: selected }))}
                    required
                  />
                </div>
                <Input label="Mobile Number *" name="mobile" type="tel" value={formData.mobile} onChange={handleChange} onBlur={handleMobileBlur} error={errors.mobile} required minLength="10" maxLength="15" />
                <Input ref={aadhaarRef} label="Aadhaar Number *" name="aadhaar_number" value={formData.aadhaar_number} onChange={handleChange} onBlur={handleAadhaarBlur} maxLength="12" error={errors.aadhaar_number} required />
                <Input ref={emailRef} label="Email *" name="email" type="email" value={formData.email} onChange={handleChange} onBlur={handleEmailBlur} error={errors.email} required />
                {otpEnabled && (
                  <div className="sm:col-span-2 rounded-xl border border-blue-200 bg-blue-50/60 p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-end">
                      <Input
                        label="Email OTP *"
                        name="email_otp"
                        value={formData.email_otp}
                        onChange={handleChange}
                        maxLength="6"
                        inputMode="numeric"
                        error={errors.email_otp}
                        className="flex-1"
                        required
                        disabled={otpVerified}
                      />
                      {!otpVerified && (
                        <button
                          type="button"
                          onClick={handleSendEmailOtp}
                          disabled={otpSending || otpVerifying || otpResendLocked || !String(formData.email || "").trim()}
                          className="w-full md:w-auto whitespace-nowrap rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                        >
                          {otpButtonText}
                        </button>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-blue-900">
                      {otpVerifying
                        ? "Verifying OTP..."
                        : otpVerified
                          ? "OTP verified. You can proceed."
                          : otpNotice || "We’ll verify your email with a one-time password before creating the account."}
                    </p>
                    {!otpVerified && otpResendLocked && (
                      <p className="mt-1 text-xs text-blue-700">
                        You can request OTP again in {formatCountdown(otpResendCooldown)}.
                      </p>
                    )}
                  </div>
                )}
                <PasswordInput
                  label="Create Password *"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength="6"
                />
                <PasswordInput
                  label="Confirm Password *"
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  required
                  minLength="6"
                  error={formData.confirm_password && formData.confirm_password !== formData.password ? "Passwords do not match" : ""}
                />
              </FormSection>

              <FormSection title="Location & Contact" number={2}>
                <Input label="Pincode *" name="pincode" value={formData.pincode} onChange={handlePincodeChange} required />
                <div>
                  <label htmlFor="postoffice" className="block text-sm font-medium text-slate-700 mb-1">Village / Post Office</label>
                  <SearchableSelect id="postoffice" name="postoffice" value={selectedPostOffice} onChange={handlePostOfficeChange} disabled={!pincodeOptions.length} className="w-full px-4 py-2 bg-slate-100 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none">
                    <option value="">{pincodeOptions.length ? "Select post office" : "Enter valid pincode"}</option>
                    {pincodeOptions.map((opt, i) => <option key={`${opt.postoffice}-${i}`} value={opt.postoffice}>{opt.postoffice}</option>)}
                  </SearchableSelect>
                </div>
                <Input label="Mandal *" name="mandal" list="mandal-opts" value={formData.mandal} onChange={handleChange} required /><datalist id="mandal-opts">{mandalOptions.map(o => <option key={o} value={o} />)}</datalist>
                <div>
                  <label htmlFor="district" className="block text-sm font-medium text-slate-700 mb-1">District *</label>
                  <SearchableSelect id="district" name="district" value={formData.district || ""} onChange={handleChange} required className="w-full px-4 py-2 bg-slate-100 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none">
                    <option value="">Select District *</option>
                    {AP_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </SearchableSelect>
                </div>

                {selectedDistrictInfo && (
                  <div className="sm:col-span-2 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5 text-xs shadow-2xs">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-amber-600 text-white rounded-lg shrink-0">
                        <FaUserTie />
                      </div>
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-900 block">
                          {selectedDistrictInfo.district_name} District Association
                        </span>
                        <span className="font-bold text-slate-900">
                          {selectedDistrictInfo.official_name}
                        </span>{" "}
                        <span className="text-slate-500">
                          ({selectedDistrictInfo.designation || "Secretary"})
                        </span>
                      </div>
                    </div>
                    {selectedDistrictInfo.mobile && (
                      <a
                        href={`tel:${selectedDistrictInfo.mobile}`}
                        className="shrink-0 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold flex items-center gap-1.5 transition shadow-2xs"
                      >
                        <FaPhoneAlt className="text-[10px]" /> Call: {selectedDistrictInfo.mobile}
                      </a>
                    )}
                  </div>
                )}
                <Input label="State *" name="state" value={formData.state} onChange={handleChange} required />
                <Input label="Full Address *" name="address" value={formData.address} onChange={handleChange} required className="sm:col-span-2" />
              </FormSection>

              <FormSection title="Sports Information" number={3}>
                <Input label="Club / Academy" name="club" value={formData.club} onChange={handleChange} />
                <Input label="Coach Name" name="coach" value={formData.coach} onChange={handleChange} />
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Select Sports *</label>
                  <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-52 overflow-y-auto rounded-lg border border-slate-200 p-3 bg-slate-50">
                    {registrationSports.map((sport) => (
                      <label key={sport.id} className="flex items-center gap-2 rounded-md bg-white border border-slate-200 px-3 py-2 text-sm">
                        <input
                          type="checkbox"
                          checked={sportProfiles.some((profile) => profile.sport_id === Number(sport.id))}
                          onChange={(e) => toggleSport(sport.id, e.target.checked)}
                        />
                        {sport.name}
                      </label>
                    ))}
                    {registrationSports.length === 0 && <span className="text-xs text-slate-500">No sports configured.</span>}
                  </div>
                </div>
                {sportProfiles.map((profile) => {
                  const sport = registrationSports.find((item) => Number(item.id) === profile.sport_id);
                  const classifications = classificationsBySport[profile.sport_id] || [];
                  return (
                    <div key={profile.sport_id} className="sm:col-span-2 rounded-xl border border-blue-200 bg-blue-50/40 p-4">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <h4 className="font-semibold text-slate-800">{sport?.name || `Sport ${profile.sport_id}`}</h4>
                        <button type="button" onClick={() => toggleSport(profile.sport_id, false)} className="text-xs font-semibold text-red-600 hover:text-red-700">Remove</button>
                      </div>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Classification *</label>
                          <select value={profile.classification_id} onChange={(e) => updateSportProfile(profile.sport_id, { classification_id: e.target.value })} className="w-full px-4 py-2 bg-white border border-slate-200 rounded-lg">
                            <option value="">Select classification</option>
                            {classifications.map((item) => <option key={item.id} value={item.id}>{item.classification} {item.discipline && item.discipline !== 'general' ? `(${item.discipline})` : ''}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Classification Type *</label>
                          <select value={profile.classification_type} onChange={(e) => updateSportProfile(profile.sport_id, { classification_type: e.target.value, certificate: e.target.value === 'New' ? null : profile.certificate })} className="w-full px-4 py-2 bg-white border border-slate-200 rounded-lg">
                            {['New', 'State', 'National', 'International'].map((type) => <option key={type} value={type}>{type}</option>)}
                          </select>
                        </div>
                        {profile.classification_type !== "New" && (
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-slate-700 mb-1">Previous Classification Certificate *</label>
                            <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={(e) => updateSportProfile(profile.sport_id, { certificate: e.target.files?.[0] || null })} className="block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" />
                            {profile.certificate && <p className="mt-1 text-xs text-slate-600">{profile.certificate.name}</p>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {sportProfiles.length === 0 && <div className="sm:col-span-2 rounded-lg border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">Select one or more sports to add their classifications.</div>}
              </FormSection>

              <FormSection title="Document Uploads" number={4}>
                <div className="sm:col-span-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  <FileInput label="Photo *" name="photo" icon="🧍" preview={previews.photo} onClear={() => handleClearFile('photo')} onChange={handleChange} accept="image/*" />
                  <FileInput label="Aadhaar *" name="aadhaar" icon="🆔" preview={previews.aadhaar} onClear={() => handleClearFile('aadhaar')} onChange={handleChange} accept=".jpg,.jpeg,.png,.pdf" />
                  <FileInput label="Birth Certificate *" name="birth_certificate" icon="🎂" preview={previews.birth_certificate} onClear={() => handleClearFile('birth_certificate')} onChange={handleChange} accept=".jpg,.jpeg,.png,.pdf" />
                  <FileInput label="SADAREM *" name="sadarem" icon="📋" preview={previews.sadarem} onClear={() => handleClearFile('sadarem')} onChange={handleChange} accept=".jpg,.jpeg,.png,.pdf" />
                  <FileInput label="Others" name="others" icon="📎" preview={previews.others} onClear={() => handleClearFile('others')} onChange={handleChange} accept=".jpg,.jpeg,.png,.pdf" />
                </div>
              </FormSection>

              {/* Aadhaar Consent & Declaration */}
              <div className="sm:col-span-2 p-4 bg-blue-50/70 border border-blue-200 rounded-xl my-2 text-left shadow-xs">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="aadhaar_consent"
                    checked={aadhaarConsent}
                    onChange={(e) => setAadhaarConsent(e.target.checked)}
                    required
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <span className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                    <strong>Aadhaar Consent & Declaration:</strong> I hereby give my voluntary consent to the Para Sports Association of Andhra Pradesh to collect and verify my Aadhaar details for identity verification, player profile registration, and sports event participation purposes only. I declare that all information provided is true to the best of my knowledge according to official records. <span className="text-red-500 font-bold">*</span>
                  </span>
                </label>
                {!aadhaarConsent && (
                  <p className="text-[11px] text-amber-700 font-medium mt-1.5 ml-7">
                    ⚠️ Please check the consent box above to proceed with registration.
                  </p>
                )}
              </div>

              <div className="sm:col-span-2 flex justify-end">
                <button type="submit" disabled={!canPreviewRegister} className="w-full sm:w-auto flex justify-center items-center gap-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-bold py-3 px-8 rounded-lg text-lg shadow-md hover:shadow-lg transition-all">
                  {loading && <FaSpinner className="animate-spin" />}
                  {loading ? "Processing..." : otpVerifying ? "Verifying OTP..." : (otpVerified || !otpEnabled) ? "Preview & Register" : "Verify OTP to Continue"}
                </button>
              </div>
            </form>
            <p className="text-sm text-center text-slate-600 mt-6">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-blue-600 hover:text-blue-700 hover:underline">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-xl font-semibold text-slate-800">Confirm Your Details</h3>
              <button type="button" onClick={() => setShowPreview(false)} className="text-slate-500 hover:text-slate-800">&times;</button>
            </div>
            <div className="p-6 overflow-y-auto space-y-6">
              {/* Simplified preview */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                {Object.entries(formData)
                  .filter(([k, v]) => !["password", "email_otp"].includes(k) && v && typeof v !== 'object')
                  .map(([k, v]) => (
                    <div key={k} className="flex">
                      <span className="font-semibold text-slate-600 w-32 capitalize">{k.replaceAll('_', ' ')}:</span>
                      <span className="text-slate-800">{v}</span>
                    </div>
                  ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 pt-2 border-t">
                <div className="col-span-full text-sm font-bold text-slate-700 mb-1">Uploaded Documents</div>
                {Object.keys(previews).map(k => (
                  <div key={k} className="flex flex-col">
                    <span className="text-xs font-semibold text-slate-600 capitalize mb-1">{k.replaceAll('_', ' ')}</span>
                    <FileTile preview={previews[k]} label={k} />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-4 p-4 border-t bg-slate-50 rounded-b-lg">
              <button type="button" onClick={() => setShowPreview(false)} className="font-semibold text-slate-700 bg-slate-200 rounded-lg px-5 py-2 hover:bg-slate-300 transition-all">Edit</button>
              <button type="button" onClick={submitRegistration} disabled={loading} className="flex items-center gap-2 font-semibold text-white bg-blue-600 rounded-lg px-5 py-2 hover:bg-blue-700 shadow-sm hover:shadow-md transition-all">
                {loading && <FaSpinner className="animate-spin" />}
                {loading ? 'Submitting...' : 'Confirm & Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
      <DistrictContactsModal
        isOpen={showDistrictModal}
        onClose={() => setShowDistrictModal(false)}
      />
    </div>
  );
};

export default PlayerRegister;
